import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Paper,
  PinInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Clock3, Mail, ShieldCheck } from "lucide-react";

import { useToastNotification } from "../../../hooks/useToast";
import { HttpCodes } from "../../../lib/data";
import { Endpoints } from "../../../lib/endpoints";
import { validateEmail } from "../../../lib/methods";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../../types";
import "../Login/styles.scss";

const FORGOT_PASSWORD_RESEND_DELAY = 30;

const ForgotPassword: React.FC = () => {
  const toast = useToastNotification();
  const [hasRequested, setHasRequested] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const forgotForm = useForm({
    initialValues: {
      email: "",
      code: "",
    },
    validate: {
      email: (value: string) =>
        validateEmail(value) ? null : "Please enter a valid email",
      code: (value: string) =>
        /^\d{6}$/.test(value) ? null : "Enter the 6-digit code",
    },
  });

  useEffect(() => {
    if (cooldown === 0) return;
    const timer = window.setInterval(() => {
      setCooldown((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const startCooldown = () => {
    setCooldown(FORGOT_PASSWORD_RESEND_DELAY);
  };

  const handleCodeRequest = async (action: "send" | "resend") => {
    const emailError = forgotForm.validateField("email");
    if (emailError) return;

    if (action === "send") {
      setIsRequesting(true);
    } else {
      setIsResending(true);
    }

    const endpoint =
      action === "send"
        ? Endpoints.GetSendForgotPasswordEmail()
        : Endpoints.GetResendForgotPasswordEmail();

    const response = await PerformRequest<NonPaginatedResponse<null>>({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        email: forgotForm.values.email,
      },
      onError: () =>
        action === "send" ? setIsRequesting(false) : setIsResending(false),
    });

    if (action === "send") {
      setIsRequesting(false);
    } else {
      setIsResending(false);
    }

    if (
      response.status === HttpCodes.Success ||
      response.status === HttpCodes.Accepted
    ) {
      toast.success(
        response.message ??
          (action === "send"
            ? "Verification code sent to your inbox"
            : "New code sent. Check your email")
      );
      setHasRequested(true);
      setIsVerified(false);
      startCooldown();
      return;
    }

    toast.error(
      response.message ?? "Unable to send verification code right now."
    );
  };

  const handleVerifyCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { hasErrors } = forgotForm.validate();
    if (hasErrors) return;

    setIsVerifying(true);
    const endpoint = Endpoints.GetVerifyForgotPassword();
    const response = await PerformRequest<NonPaginatedResponse<null>>({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        email: forgotForm.values.email,
        otp: forgotForm.values.code,
      },
      onError: () => setIsVerifying(false),
    });
    setIsVerifying(false);

    if (
      response.status === HttpCodes.Success ||
      response.status === HttpCodes.Accepted
    ) {
      setIsVerified(true);
      toast.success(
        response.message ??
          "Code confirmed. Follow the instructions sent to your email."
      );
      return;
    }

    toast.error(response.message ?? "Invalid or expired code. Try again.");
  };

  const handleCodeChange = (value: string) => {
    const sanitized = value.replace(/\D/g, "").slice(0, 6);
    forgotForm.setFieldValue("code", sanitized);
  };

  const resendLabel =
    cooldown > 0
      ? `Resend in 0:${String(cooldown).padStart(2, "0")}`
      : "Resend code";

  return (
    <div className="login-page">
      <div className="login-page__background">
        <span className="glow glow--one" />
        <span className="glow glow--two" />
        <span className="grid-overlay" />
        <span className="noise" />
      </div>

      <Container size="xl" className="login-page__container">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Stack className="login-page__hero" gap="lg">
            <Badge
              variant="gradient"
              gradient={{ from: "#ab7cff", to: "#57d2ff" }}
              size="lg"
              radius="md"
              leftSection={<ShieldCheck size={16} />}
            >
              Secure recovery
            </Badge>

            <Title order={1} className="login-page__title" c="white">
              Recover access with a{" "}
              <Text component="span" inherit className="gradient-text">
                six-digit passcode
              </Text>
            </Title>

            <Text size="lg" c="white" maw={520} opacity={0.8}>
              We&apos;ll email you a one-time passcode to prove you own the
              account. Enter it here to continue resetting your password without
              waiting on support.
            </Text>
          </Stack>

          <Paper
            shadow="xl"
            radius="lg"
            p="xl"
            className="login-page__card"
            withBorder
          >
            <Stack gap="md">
              <div>
                <Title order={3}>Forgot password</Title>
                <Text size="sm" c="dimmed">
                  Request a verification code and confirm it to continue with
                  your password reset.
                </Text>
              </div>

              <form onSubmit={handleVerifyCode}>
                <Stack gap="md">
                  <TextInput
                    label="Email address"
                    placeholder="you@company.com"
                    size="lg"
                    radius="sm"
                    leftSection={<Mail size={16} />}
                    {...forgotForm.getInputProps("email")}
                  />

                  <Group gap="sm" align="flex-end">
                    <Button
                      type="button"
                      radius="sm"
                      size="lg"
                      onClick={() => handleCodeRequest("send")}
                      loading={isRequesting}
                    >
                      Request code
                    </Button>
                    <Button
                      type="button"
                      variant="light"
                      radius="sm"
                      size="lg"
                      onClick={() => handleCodeRequest("resend")}
                      disabled={!hasRequested || cooldown > 0}
                      loading={isResending}
                    >
                      {resendLabel}
                    </Button>
                  </Group>

                  {!hasRequested && (
                    <Text size="sm" c="dimmed">
                      We&apos;ll send a six-digit code to the email above. You
                      can request another after 30 seconds.
                    </Text>
                  )}

                  {hasRequested && (
                    <Stack gap="sm">
                      <Text size="sm" c="dimmed">
                        Enter the six-digit code sent to{" "}
                        <Text component="span" fw={600}>
                          {forgotForm.values.email}
                        </Text>
                      </Text>
                      <PinInput
                        length={6}
                        type="number"
                        size="lg"
                        radius="sm"
                        inputMode="numeric"
                        value={forgotForm.values.code}
                        onChange={(value) => handleCodeChange(value)}
                        error={!!forgotForm.errors.code}
                        oneTimeCode
                      />
                      <Button
                        type="submit"
                        radius="sm"
                        size="lg"
                        loading={isVerifying}
                      >
                        Confirm code
                      </Button>
                    </Stack>
                  )}

                  {isVerified && (
                    <Card radius="md" className="login-page__status" withBorder>
                      <Group justify="space-between">
                        <Text size="sm" fw={600}>
                          Code verified
                        </Text>
                        <Badge color="green" variant="dot">
                          Success
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed" mt={4}>
                        Check your email for the reset link or continue to the
                        next step in the process.
                      </Text>
                    </Card>
                  )}
                </Stack>
              </form>

              <Text size="sm" c="dimmed" ta="center">
                Remembered your password?{" "}
                <Anchor component={Link} to="/login" size="sm" c="cyan">
                  Back to login
                </Anchor>
              </Text>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Container>
    </div>
  );
};

export default ForgotPassword;
